import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

/**
 * Per-source state returned by GET /api/sources/health.
 *
 * Mirrors the backend ``services/circuit_breaker.CircuitBreaker.snapshot``
 * shape. The frontend only consumes ``state`` today (banner trigger);
 * the rest is exposed so debug screens / dev tools can surface the full
 * picture without another roundtrip.
 */
export interface SourceHealth {
  state: "closed" | "open" | "half_open";
  failures: number;
  opened_at: string | null;
  last_failure_reason: string | null;
  failure_threshold: number;
  open_for_seconds: number;
}

export type SourcesHealthResponse = Record<string, SourceHealth>;

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Poll the backend's circuit breaker registry every 5 min.
 *
 * Why 5 min: matches the typical breaker cool-down (300 s) — polling
 * faster would mostly return the same payload. The banner doesn't need
 * to flip instantly; a 5-minute lag is invisible operationally and
 * keeps the network noise floor near zero.
 *
 * The endpoint is in-memory and unauthenticated; failing the request
 * (network down, server 500) returns ``undefined`` so the banner stays
 * hidden — silent degradation of THIS signal is acceptable because the
 * ConnectivityBanner already surfaces network/server failures with
 * higher specificity.
 */
export function useSourceHealth() {
  return useQuery<SourcesHealthResponse>({
    queryKey: ["/sources/health"],
    queryFn: async () => {
      const r = await api.get("/sources/health");
      return r.data as SourcesHealthResponse;
    },
    staleTime: FIVE_MINUTES,
    refetchInterval: FIVE_MINUTES,
    // Don't retry — if it failed, just wait for the next interval. A
    // failing health endpoint is itself a degraded signal that the
    // dedicated ConnectivityBanner will surface.
    retry: false,
    // Avoid burst polls when the tab regains focus.
    refetchOnWindowFocus: false,
  });
}

/** Names of sources currently NOT in the closed state. Empty = all healthy. */
export function degradedSources(data: SourcesHealthResponse | undefined): string[] {
  if (!data) return [];
  return Object.entries(data)
    .filter(([, v]) => v?.state && v.state !== "closed")
    .map(([k]) => k);
}
